import {Category, Logger} from '@simpleinvest/front-end-logger';
import {INTERCEPTOR_HANDLED_STATUSES, LOG_SKIPPED_STATUSES} from '@advisor/util/HttpStatus';
import {InvalidErrorObject, InvalidResponse} from '@advisor/util/customExceptions';
import {MicroserviceError} from '@digital-advice/ms-core-sdk';

const getErrorLocation = (error) => {
  let location = '';

  // eslint-disable-next-line no-prototype-builtins
  if (!Error.prototype.isPrototypeOf(error)) {
    return location;
  }

  try {
    location = error.config.url
      .split(/(v\d\/)/)[2]
      .split('?')
      .shift();
  } catch {
    return location;
  }

  location = location.replaceAll(/\d+/g, '##');

  return location;
};

const getSeverity = () => 'error';

/**
 *
 * @param {String} message
 * @param {Error} error
 */
const getInvalidException = (message, error) => new InvalidErrorObject(message, error);

const logError = (message, error, isWarning = false) => {
  const severity = isWarning ? 'warning' : getSeverity();

  const logger = new Logger({
    category: Category.Advisors,
    fileName: message,
  });

  logger.logException({
    error: getInvalidException(message, error),
    level: severity,
  });

  logger.addExtra({key: 'Error Object', value: error});
};

const handleInterceptorException = (exceptionName, error) => {
  const errorlocation = getErrorLocation(error);

  const logger = new Logger({
    category: Category.Advisors,
    fileName: errorlocation,
  });

  logger.logMessage({
    message: `${exceptionName} at ${errorlocation}`,
    fingerprint: ['{{default}}', errorlocation],
    level: getSeverity(),
  });

  logger.addExtra({error});
};

const handleInvalidResponse = (errorLocation, error) => {
  const logger = new Logger({
    category: Category.Advisors,
    fileName: errorLocation,
  });

  logger.logMessage({
    message: `${error.name} from ${errorLocation}`,
    level: getSeverity(),
  });

  logger.addExtra({key: 'Error Object', value: error});
};

const handleInternalServerError = (message, error) => {
  const traceId = error.response.data.traceId || '-';
  const errorLocation = getErrorLocation(error);

  const logger = new Logger({
    category: Category.Advisors,
    fileName: errorLocation,
  });

  logger.logMessage({
    message: `${message} at ${errorLocation}`,
    fingerprint: ['{{default}}', errorLocation],
    level: getSeverity(),
    tags: [{key: 'trace_id', value: traceId}],
  });

  logger.addExtra({key: 'Error Object', value: error});
};

/**
 *
 * @param {String} serviceName
 * @param {Number} statusCode
 * @param {Object} error
 */
const handleXhrException = (serviceName, statusCode, error) => {
  if ([...INTERCEPTOR_HANDLED_STATUSES, ...LOG_SKIPPED_STATUSES].includes(statusCode)) {
    return;
  }

  logError(serviceName, error);
};

/**
 *
 * @param {String} callingFuncName
 * @param {Object} error
 * @param {Boolean?} bubbleUp
 */
const handleGenericException = (callingFuncName, error, bubbleUp = false, isWarning = false) => {
  // eslint-disable-next-line no-prototype-builtins
  if (!MicroserviceError.prototype.isPrototypeOf(error)) {
    if (error?.response?.status) {
      handleXhrException(callingFuncName, error.response.status, error);
      // in case we need to alert the user of the error, we'll bubble up the error for UI purposes here
      if (bubbleUp) {
        throw error;
      }

      return;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (InvalidResponse.prototype.isPrototypeOf(error)) {
      handleInvalidResponse(callingFuncName, error);

      return;
    }
  }

  // front end exceptions
  logError(`Error from ${callingFuncName}.`, error, isWarning);
};

export default {
  handleGenericException,
  handleXhrException,
  handleInterceptorException,
  handleInternalServerError,
  handleInvalidResponse,
};
